import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import api from '../api';
import useServerDraft from '../hooks/useServerDraft';
import { INVITATION_TEMPLATES, shiftTime, minutesOf } from '../data/invitationTemplates';

const PROJECT_LOGOS = {
  'ד החדשה': '/logos/jaffad.png',
  'אייזיקחריף': '/logos/jaffag.png',
  'ג החדשה': '/logos/jaffag.png',
  'סקייליין בת ים': '/logos/skyline.png',
  'ראשון החדשה': '/logos/rishon.jpg',
  'אפלטון 6': '/logos/aplaton6.jpeg',
  'שארית ישראל': '/logos/sherit_israel.jpg',
};

const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const C = {
  dark: '#164a52', mid: '#4a9a96', light: '#5ec8c6', pale: '#e9f5fa', gold: '#e3a73a', orange: '#dd8a2f',
  eyebrow: '#8fc5c9', line: '#d6e9ef', text: '#1c3a40', muted: '#6b7f84',
};

function buildDateText(eventDate) {
  const m = String(eventDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `יום ${WEEKDAYS[new Date(+y, +mo - 1, +d).getDay()]}, ${+d}.${+mo}.${y}`;
}

const inp = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', boxSizing: 'border-box', fontFamily: 'inherit', direction: 'rtl' };
const lbl = { fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 };

function templateDefaults(tpl, projectName) {
  return {
    templateId: tpl.id, title: tpl.title, tagline: tpl.tagline, endTime: tpl.endTime,
    agenda: tpl.agenda.map(a => ({ ...a })),
    projectName: projectName || '',
  };
}

export default function InvitationBuilder({ projectName, enc, eventDate, eventHour, eventMinute, onUploaded }) {
  const previewRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useServerDraft(enc, 'invitation2', {
    ...templateDefaults(INVITATION_TEMPLATES[0], projectName),
    projectLogo: PROJECT_LOGOS[projectName] || '', developerLogo: '',
    location: '', dateText: '', timeText: '',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const eventTime = `${String(eventHour).padStart(2, '0')}:${eventMinute}`;
  const dateShown = f.dateText || buildDateText(eventDate) || '—';
  const timeShown = f.timeText || (eventDate ? eventTime : '—');

  function chooseTemplate(id) {
    const tpl = INVITATION_TEMPLATES.find(t => t.id === id);
    if (tpl) setF(p => ({ ...p, ...templateDefaults(tpl, p.projectName || projectName) }));
  }

  function syncTimesToEvent() {
    const delta = minutesOf(eventTime) - minutesOf(f.agenda[0]?.time);
    setF(p => ({ ...p, agenda: p.agenda.map(a => ({ ...a, time: shiftTime(a.time, delta) })), endTime: shiftTime(p.endTime, delta) }));
  }

  const updateItem = (i, k, v) => setF(p => ({ ...p, agenda: p.agenda.map((a, j) => (j === i ? { ...a, [k]: v } : a)) }));
  const removeItem = i => setF(p => ({ ...p, agenda: p.agenda.filter((_, j) => j !== i) }));
  const addItem = () => setF(p => ({ ...p, agenda: [...p.agenda, { time: '', title: '', sub: '' }] }));

  async function uploadImage(file) {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/portal/events/${enc}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return res.data.url;
    } catch { alert('שגיאה בהעלאת התמונה'); return null; }
  }

  async function createPdf() {
    if (!previewRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', (pageW - canvas.width * ratio) / 2, 0, canvas.width * ratio, canvas.height * ratio);
      const fd = new FormData();
      fd.append('file', new File([pdf.output('blob')], 'invitation.pdf', { type: 'application/pdf' }));
      const res = await api.post(`/portal/events/${enc}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(res.data.url);
    } catch { alert('שגיאה ביצירת ה-PDF'); }
    setBusy(false);
  }

  const gradientBar = { background: `linear-gradient(90deg, ${C.mid}, ${C.light} 35%, ${C.gold} 75%, ${C.orange})` };

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 320px', display: 'grid', gap: 10 }}>
        <div>
          <label style={lbl}>סוג הכנס (תבנית)</label>
          <select style={inp} value={f.templateId} onChange={e => chooseTemplate(e.target.value)}>
            {INVITATION_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 3 }}>החלפת תבנית טוענת מחדש את הכותרת וסדר היום שלה</div>
        </div>
        <div><label style={lbl}>שם הפרויקט</label><input style={inp} value={f.projectName} onChange={e => set('projectName', e.target.value)} /></div>
        <div>
          <label style={lbl}>לוגו הפרויקט (בראש הדף)</label>
          <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0]).then(v => v && set('projectLogo', v))} />
          {f.projectLogo && <button type="button" onClick={() => set('projectLogo', '')} style={{ marginRight: 8, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem' }}>הסר</button>}
        </div>
        <div>
          <label style={lbl}>לוגו היזם (יוצג ליד הלוגו של קומיוניטי)</label>
          <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0]).then(v => v && set('developerLogo', v))} />
          {f.developerLogo && <button type="button" onClick={() => set('developerLogo', '')} style={{ marginRight: 8, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem' }}>הסר</button>}
        </div>
        <div><label style={lbl}>כותרת</label><input style={inp} value={f.title} onChange={e => set('title', e.target.value)} /></div>
        <div><label style={lbl}>שורת משנה</label><input style={inp} value={f.tagline} onChange={e => set('tagline', e.target.value)} /></div>
        <div>
          <label style={lbl}>תאריך (ריק = לפי תאריך הכנס)</label>
          <input style={inp} value={f.dateText} onChange={e => set('dateText', e.target.value)} placeholder={buildDateText(eventDate)} />
        </div>
        <div>
          <label style={lbl}>שעה (ריק = לפי שעת הכנס)</label>
          <input style={inp} value={f.timeText} onChange={e => set('timeText', e.target.value)} placeholder={eventDate ? eventTime : '19:00'} />
        </div>
        <div><label style={lbl}>מיקום הכנס</label><input style={inp} value={f.location} onChange={e => set('location', e.target.value)} placeholder="לדוגמה: אולם הספורט, רחוב הרצל 5" /></div>

        <div style={{ fontWeight: 700, fontSize: '0.85rem', marginTop: 6 }}>סדר היום</div>
        {f.agenda.map((a, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '64px 1fr auto', gap: 6, alignItems: 'start', borderBottom: '1px dashed #e5e7eb', paddingBottom: 6 }}>
            <input style={{ ...inp, padding: '6px 4px', textAlign: 'center' }} value={a.time} onChange={e => updateItem(i, 'time', e.target.value)} placeholder="19:00" />
            <div style={{ display: 'grid', gap: 4 }}>
              <input style={inp} value={a.title} onChange={e => updateItem(i, 'title', e.target.value)} placeholder="נושא" />
              <input style={{ ...inp, fontSize: '0.8rem' }} value={a.sub} onChange={e => updateItem(i, 'sub', e.target.value)} placeholder="פירוט (לא חובה)" />
            </div>
            <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem' }}>🗑</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={addItem} style={{ background: 'none', border: '1px dashed #9ca3af', color: '#6b7280', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>+ הוסף סעיף</button>
          <button type="button" onClick={syncTimesToEvent} disabled={!eventDate} style={{ background: '#f3f4f6', border: 'none', color: '#374151', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>⏱ התאם שעות לשעת הכנס</button>
        </div>
        <div><label style={lbl}>סיום משוער</label><input style={inp} value={f.endTime} onChange={e => set('endTime', e.target.value)} placeholder="20:40" /></div>

        <button onClick={createPdf} disabled={busy} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
          {busy ? 'יוצר PDF...' : '📄 צור PDF וצרף להזמנה'}
        </button>
      </div>

      <div style={{ flex: '0 0 auto', maxWidth: '100%', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <div ref={previewRef} style={{ width: 794, minHeight: 1123, background: 'white', color: C.text, direction: 'rtl', fontFamily: 'Heebo, Arial, sans-serif', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 22, ...gradientBar }} />

          <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {f.projectLogo
              ? <img src={f.projectLogo} alt="" style={{ maxHeight: 96, maxWidth: 300, objectFit: 'contain' }} />
              : <div style={{ width: 290, height: 86, border: `2px dashed ${C.light}`, borderRadius: 14, color: C.mid, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>מקום ללוגו הפרויקט</div>}
          </div>

          <div style={{ position: 'relative', background: C.dark, height: 210, overflow: 'hidden', borderBottom: `7px solid ${C.gold}`, boxSizing: 'content-box' }}>
            <div style={{ position: 'absolute', left: -48, top: -50, width: 240, height: 240, borderRadius: '50%', background: `linear-gradient(180deg, #c98736, #dcb04b)` }} />
            <div style={{ position: 'absolute', left: 112, bottom: 0, width: 128, height: 112, background: 'rgba(255,255,255,0.18)', clipPath: 'polygon(50% 0, 0 100%, 100% 100%)' }} />
            <div style={{ position: 'relative', padding: '30px 46px 0 46px', textAlign: 'right' }}>
              <div style={{ color: C.eyebrow, fontSize: 15, letterSpacing: 1.5 }}>קבוצת קומיוניטי · התחדשות עירונית</div>
              <div style={{ color: 'white', fontSize: f.title.length > 26 ? 38 : 46, fontWeight: 800, lineHeight: 1.2, margin: '12px 0 14px' }}>{f.title}</div>
              <div style={{ color: C.eyebrow, fontSize: 16 }}>{f.projectName}{f.projectName && f.tagline ? '  |  ' : ''}{f.tagline}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 14, padding: '24px 46px 0' }}>
            {[['תאריך', dateShown], ['שעה', timeShown], ['מיקום', f.location || '—']].map(([label, value]) => (
              <div key={label} style={{ flex: 1, background: C.pale, borderRadius: 12, borderRight: `5px solid ${C.light}`, padding: '12px 16px', minWidth: 0 }}>
                <div style={{ color: C.mid, fontSize: 13, fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2, wordBreak: 'break-word', lineHeight: 1.25 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: '22px 46px 0', flex: 1, minHeight: 0 }}>
            <div style={{ fontSize: 28, fontWeight: 800 }}>סדר היום</div>
            <div style={{ width: 58, height: 4, background: C.orange, borderRadius: 2, margin: '6px 0 6px' }} />
            {f.agenda.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '9px 0', borderBottom: `1px solid ${C.line}` }}>
                <div style={{ width: 76, flexShrink: 0, textAlign: 'center', background: i % 2 ? C.mid : C.dark, color: 'white', borderRadius: 18, padding: '6px 0', fontWeight: 700, fontSize: 16 }}>{a.time}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 19, fontWeight: 800 }}>{a.title}</div>
                  {a.sub && <div style={{ fontSize: 14, color: C.muted, marginTop: 1 }}>{a.sub}</div>}
                </div>
              </div>
            ))}
            {f.endTime && <div style={{ marginTop: 8, fontSize: 14, color: C.mid }}>סיום משוער: <b>{f.endTime}</b></div>}
          </div>

          <div style={{ height: 8, ...gradientBar }} />
          <div style={{ background: C.dark, height: 138, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22, direction: 'rtl' }}>
              <img src="/logos/community_white.png" alt="Community Group" style={{ height: 66, objectFit: 'contain' }} />
              {f.developerLogo && (
                <div style={{ background: 'white', borderRadius: 10, padding: '6px 12px', display: 'flex', alignItems: 'center' }}>
                  <img src={f.developerLogo} alt="" style={{ height: 52, maxWidth: 170, objectFit: 'contain' }} />
                </div>
              )}
              <div style={{ width: 2, height: 62, background: 'rgba(255,255,255,0.25)' }} />
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: 'white', fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>אור לוי</div>
                <div style={{ color: C.eyebrow, fontSize: 14 }}>מנכ"ל קבוצת קומיוניטי</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', color: C.eyebrow, fontSize: 12 }}>communityltd.co.il</div>
          </div>
        </div>
      </div>
    </div>
  );
}
